// TabNav — URL-state-driven tabbed sub-section nav primitive (CHG-1233 / pack 2).
//
// Reads/writes `?<paramName>=` (default "tab") via useSearchParams so refresh
// + deep-linking preserve state. Pure tab strip — the parent owns rendering
// the active tab's body (this primitive returns the active key via the
// optional `onActiveChange` callback, OR the parent reads searchParams
// itself).
//
// Per feedback_tabnav_url_state.md (per brief v5b): URL is canonical for tab
// state. Per feedback_composition_system.md: slot composition only — no
// business logic.

import { useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";

export interface TabDef {
  key: string;
  label: string;
  badge?: number | string;
  disabled?: boolean;
}

export interface TabNavProps {
  tabs: TabDef[];
  /** Tab key applied when URL param is absent (or no value matches). */
  defaultTab?: string;
  /** Search-param name. Default: "tab". */
  paramName?: string;
  /** Optional callback fired with the resolved active key on mount and changes. */
  onActiveChange?: (key: string) => void;
}

export function TabNav({ tabs, defaultTab, paramName = "tab", onActiveChange }: TabNavProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlValue = searchParams.get(paramName);
  const fallback = defaultTab ?? tabs[0]?.key ?? "";
  const activeKey = useMemo(() => {
    if (urlValue && tabs.some((t) => t.key === urlValue)) return urlValue;
    return fallback;
  }, [urlValue, fallback, tabs]);

  // Stash onActiveChange in a ref so callers don't have to memoize it — depending
  // on it directly creates an infinite-loop hazard if the callback triggers a
  // parent re-render that returns a new fn reference.
  const onActiveChangeRef = useRef(onActiveChange);
  useEffect(() => {
    onActiveChangeRef.current = onActiveChange;
  });
  useEffect(() => {
    onActiveChangeRef.current?.(activeKey);
  }, [activeKey]);

  const select = (key: string) => {
    if (key === activeKey) return;
    const next = new URLSearchParams(searchParams);
    next.set(paramName, key);
    setSearchParams(next, { replace: false });
  };

  return (
    <div
      role="tablist"
      aria-label="Page sections"
      style={{
        display: "flex",
        gap: 4,
        borderBottom: "1px solid var(--color-border-subtle)",
        marginBottom: 16,
      }}
    >
      {tabs.map((tab) => {
        const active = tab.key === activeKey;
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={`tabpanel-${tab.key}`}
            id={`tab-${tab.key}`}
            disabled={tab.disabled}
            onClick={() => select(tab.key)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "10px 14px",
              background: "transparent",
              border: "none",
              borderBottom: active ? "2px solid var(--color-text)" : "2px solid transparent",
              marginBottom: -1,
              fontSize: 13,
              fontWeight: active ? 600 : 500,
              color: active
                ? "var(--color-text)"
                : tab.disabled
                  ? "var(--color-text-subtle)"
                  : "var(--color-text-muted)",
              cursor: tab.disabled ? "not-allowed" : "pointer",
              opacity: tab.disabled ? 0.6 : 1,
              transition: "color 0.12s ease, border-color 0.12s ease",
            }}
          >
            {tab.label}
            {tab.badge !== undefined && tab.badge !== null && tab.badge !== 0 ? (
              <span
                aria-hidden="true"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  height: 18,
                  padding: "0 6px",
                  borderRadius: 9999,
                  fontSize: 11,
                  fontWeight: 600,
                  background: active ? "var(--color-text)" : "var(--color-bg-subtle)",
                  color: active ? "white" : "var(--color-text-muted)",
                }}
              >
                {tab.badge}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
